// Sophia — Sovereign Relay
// Always-on background privacy layer. A browser tab cannot run a kernel-level
// VPN, so this is the strongest equivalent that works fully in-page: every
// outbound fetch Sophia makes is routed through a rotating chain of relay
// exits (and Tor .onion bridge gateways when a target is an onion address),
// so origin servers see the relay, not the device.

export type RelayExit = { id: string; label: string; wrap: (url: string) => string; onion?: boolean };

const STORE = "sophia:relay";

/** Same-origin exit: Sophia's own server relay. It has no CORS problem and no
 *  third-party uptime risk, so it is the default and the last-resort fallback
 *  when every public proxy is down. */
export const SELF_EXIT: RelayExit = {
  id: "self",
  label: "Sophia relay exit (same-origin)",
  wrap: (u) => `/api/public/relay?url=${encodeURIComponent(u)}`,
};

export const RELAY_EXITS: RelayExit[] = [
  SELF_EXIT,
  { id: "jina", label: "Reader exit (jina)", wrap: (u) => `https://r.jina.ai/${u}` },
  { id: "allorigins", label: "AllOrigins exit", wrap: (u) => `https://api.allorigins.win/raw?url=${encodeURIComponent(u)}` },
  { id: "corsproxy", label: "CorsProxy exit", wrap: (u) => `https://corsproxy.io/?${encodeURIComponent(u)}` },
  { id: "codetabs", label: "CodeTabs exit", wrap: (u) => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(u)}` },
];

// Tor bridge gateways — used automatically for .onion targets.
// `host` is the gateway domain, probed directly (an .onion target isn't needed
// to tell whether the gateway itself is alive).
export const TOR_BRIDGES: Array<RelayExit & { host: string }> = [
  { id: "tor-ws", label: "Tor bridge (onion.ws)", onion: true, host: "onion.ws", wrap: (u) => u.replace(/\.onion/i, ".onion.ws") },
  { id: "tor-ly", label: "Tor bridge (onion.ly)", onion: true, host: "onion.ly", wrap: (u) => u.replace(/\.onion/i, ".onion.ly") },
  { id: "tor-moe", label: "Tor bridge (onion.moe)", onion: true, host: "onion.moe", wrap: (u) => u.replace(/\.onion/i, ".onion.moe") },
];


export type RelayState = {
  enabled: boolean;
  torEnabled: boolean;
  exitId: string;
  rotateMs: number;
  lastRotate: number;
  circuits: number;
  lastLatency: number | null;
  online: boolean;
};

function load(): RelayState {
  const base: RelayState = {
    enabled: true,
    torEnabled: true,
    exitId: RELAY_EXITS[0].id,
    rotateMs: 5 * 60_000,
    lastRotate: 0,
    circuits: 0,
    lastLatency: null,
    online: true,
  };
  if (typeof window === "undefined") return base;
  try {
    return { ...base, ...JSON.parse(localStorage.getItem(STORE) ?? "{}") };
  } catch {
    return base;
  }
}

let state: RelayState = load();
const listeners = new Set<(s: RelayState) => void>();

function save() {
  try { localStorage.setItem(STORE, JSON.stringify(state)); } catch { /* quota */ }
  listeners.forEach((l) => l({ ...state }));
}

export function getRelayState(): RelayState { return { ...state }; }
export function onRelayChange(fn: (s: RelayState) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function setRelayEnabled(on: boolean) { state.enabled = on; save(); }
export function setTorEnabled(on: boolean) { state.torEnabled = on; save(); }
export function setRelayExit(id: string) { state.exitId = id; state.circuits += 1; save(); }
export function setRotateMs(ms: number) { state.rotateMs = Math.max(30_000, ms); save(); }

/** Pick a new random exit — a fresh "circuit". */
export function rotateCircuit(): RelayExit {
  const others = RELAY_EXITS.filter((e) => e.id !== state.exitId);
  const next = others[Math.floor(Math.random() * others.length)] ?? RELAY_EXITS[0];
  state.exitId = next.id;
  state.lastRotate = Date.now();
  state.circuits += 1;
  save();
  return next;
}

export function currentExit(): RelayExit {
  return RELAY_EXITS.find((e) => e.id === state.exitId) ?? RELAY_EXITS[0];
}

/** Rewrite a URL so it leaves through the active relay / Tor bridge. */
export function relayUrl(url: string): string {
  if (!state.enabled) return url;
  if (/\.onion(\/|$|:)/i.test(url)) {
    if (!state.torEnabled) return url;
    const bridge = TOR_BRIDGES[state.circuits % TOR_BRIDGES.length];
    return bridge.wrap(url);
  }
  return currentExit().wrap(url);
}

/** Public proxies cannot be hit directly from a page — the browser blocks them
 *  on CORS, which reads as "Failed to fetch" and costs a wasted request every
 *  single time. So every third-party exit leaves through Sophia's own relay:
 *  same-origin, no CORS, and the exit is still the one facing the target. */
function throughSelf(target: string): string {
  return target.startsWith("/") ? target : `/api/public/relay?url=${encodeURIComponent(target)}`;
}

/** An exit that just failed is benched, so a dead proxy is tried once every few
 *  minutes instead of on every fetch. This is what stops the error storms. */
const benched = new Map<string, number>();
const BENCH_MS = 15 * 60_000;
/** How many times in a row an exit has refused — the bench grows with it, so a
 *  permanently dead proxy is asked once an hour, not thousands of times a day. */
const strikes = new Map<string, number>();
function bench(id: string) {
  const n = (strikes.get(id) ?? 0) + 1;
  strikes.set(id, n);
  benched.set(id, Date.now() + Math.min(BENCH_MS * 2 ** Math.min(n - 1, 2), 60 * 60_000));
}
function live(exit: RelayExit): boolean {
  const until = benched.get(exit.id) ?? 0;
  if (until > Date.now()) return false;
  benched.delete(exit.id);
  return true;
}
/** True when every third-party exit is currently benched. */
export function allExitsBenched(): boolean {
  return RELAY_EXITS.filter((e) => e.id !== SELF_EXIT.id).every((e) => !live(e));
}

/** Fetch through the relay, transparently falling back across exits. */
export async function relayFetch(url: string, init?: RequestInit): Promise<Response> {
  if (!state.enabled) return fetch(url, init);
  // The relay is a read-only GET proxy; anything else goes direct.
  const method = (init?.method ?? "GET").toUpperCase();
  if (method !== "GET") return fetch(url, init);
  // No signal: asking six exits in turn cannot succeed and only produces noise.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    state.online = false;
    throw new Error("offline");
  }


  const started = performance.now();
  const ordered = [currentExit(), ...RELAY_EXITS.filter((e) => e.id !== state.exitId)];
  const chain = ordered.filter(live);
  // Everything benched: fall back to the one exit that is always reachable.
  if (chain.length === 0) chain.push(SELF_EXIT);

  let lastErr: unknown = null;
  for (const exit of chain) {
    try {
      const wrapped = /\.onion(\/|$|:)/i.test(url) ? relayUrl(url) : exit.wrap(url);
      const res = await fetch(throughSelf(wrapped), init);
      if (!res.ok) throw new Error(`exit ${exit.id} → ${res.status}`);
      state.exitId = exit.id;
      state.lastLatency = Math.round(performance.now() - started);
      state.online = true;
      save();
      return res;
    } catch (e) {
      lastErr = e;
      bench(exit.id);
    }
  }
  state.online = false;
  save();
  throw lastErr ?? new Error("All relay exits failed");
}

/** Which exits are currently benched, and for how long — for the ops panels. */
export function benchedExits(): Array<{ id: string; secondsLeft: number }> {
  const now = Date.now();
  return [...benched.entries()]
    .filter(([, until]) => until > now)
    .map(([id, until]) => ({ id, secondsLeft: Math.round((until - now) / 1000) }));
}


let timer: number | null = null;
/** Consecutive failed warm probes — the probe slows right down after a few. */
let warmFails = 0;
let tickCount = 0;

/** Keep the relay warm in the background: rotate circuits, probe health. */
export function startRelay(): void {
  if (typeof window === "undefined" || timer !== null) return;
  const tick = async () => {
    if (!state.enabled) return;
    // Hard embargo while offline radio learning runs — no probes leave the device.
    const { isCapabilityOn } = await import("./capabilities");
    if (isCapabilityOn("offlineRadio")) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) { state.online = false; return; }
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    tickCount += 1;
    // After three failures in a row the warm probe drops to once every ten
    // minutes, and once every half hour after ten — a dead exit is never asked
    // in a tight loop again.
    const every = warmFails >= 10 ? 30 : warmFails >= 3 ? 10 : 1;
    if (tickCount % every !== 0) return;
    if (Date.now() - state.lastRotate > state.rotateMs) rotateCircuit();
    try {
      const t0 = performance.now();
      const warm = currentExit().wrap("https://example.com");
      // Same-origin probe: a direct hit on a third-party proxy is blocked by
      // CORS in-page, which used to look like the exit being dead.
      const res = await fetch(warm.startsWith("/") ? warm : `/api/public/relay?url=${encodeURIComponent(warm)}`, { method: "GET", cache: "no-store" });
      if (!res.ok) throw new Error(`warm probe ${res.status}`);
      state.lastLatency = Math.round(performance.now() - t0);
      state.online = true;
      warmFails = 0;
    } catch {
      state.online = false;
      warmFails += 1;
      bench(currentExit().id);
      if (warmFails <= 3) rotateCircuit();
    }
    save();
  };
  void tick();
  timer = window.setInterval(tick, 60_000);
}

export function stopRelay(): void {
  if (timer !== null) { window.clearInterval(timer); timer = null; }
}
